import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router';
import { useStore } from '../../store/store';
import { HEADER_MESSAGE_TYPES, SESSION_DISPLACED_MESSAGE } from '../../utils/constants/header';
import { requestSessionClaim } from '../../utils/sessionClaim';

// Delay before re-claiming once on a denied login claim. Absorbs a colleague's
// in-flight /session-end (logout) from another machine so the freed slot is
// picked up without flashing a false take-over modal (TD-2239).
const RECLAIM_RETRY_MS = 1500;

/**
 * Owns the concurrent-session lifecycle on the app shell (TD-2239). Mounted once
 * by SessionLimitModalProvider — inside the Router (so useLocation works) and
 * the modal context (the Provider passes `openModal`).
 *
 * Behaviour:
 *  - On login (authStore.user becomes set): POST /session-claim. `allowed` →
 *    the caller holds a slot, proceed. Denied → retry once after a short delay
 *    (a colleague who just logged out on ANOTHER machine may have a /session-end
 *    still in flight; the retry lets it commit so the freed slot is picked up
 *    cleanly instead of showing a false modal) → still denied → open the blocking
 *    take-over modal (Take over → force claim; Cancel → logout).
 *  - On window focus + route change WHILE holding a slot: re-claim (force:false,
 *    so it refreshes/re-claims — never kicks). If denied, the caller was
 *    displaced (a colleague took the slot) → force-logout with a message.
 *
 * No periodic heartbeat — detection is on the user's next interaction. The
 * freed slot makes an idle stale tab harmless; re-entry requires a deliberate
 * re-login, so there is no one-click ping-pong.
 */
const SessionClaimController = observer(({ openModal }) => {
  const store = useStore();
  const { authStore, utilsStore } = store;
  const { pathname } = useLocation();
  const claimedRef = useRef(false);
  const recheckingRef = useRef(false);
  const loggedIn = !!authStore.user;

  // Login claim — runs once when the user becomes authenticated.
  useEffect(() => {
    if (!loggedIn) {
      claimedRef.current = false;
      return undefined;
    }
    // A successful login supersedes any stale "you were signed out because a
    // colleague took over" notice left on the login screen — clear it so it
    // doesn't persist into the freshly-authenticated session (TD-2239).
    utilsStore.clearHeaderMessage();
    let cancelled = false;
    let retryTimer = null;

    const openTakeoverModal = (res) => {
      if (cancelled) return;
      openModal({
        oldestEmail: res.oldest_email,
        onTakeoverSuccess: () => {
          claimedRef.current = true;
        },
        onCancel: () => authStore.logout()
      });
    };

    // Handles the result of the single re-claim retry below. Hoisted out of the
    // setTimeout/then chain to keep nesting shallow (Sonar S2004).
    const handleRetryResult = (retry) => {
      if (cancelled) return;
      if (retry.allowed) {
        claimedRef.current = true;
      } else {
        openTakeoverModal(retry);
      }
    };

    requestSessionClaim(store, false).then((res) => {
      if (cancelled) return;
      if (res.allowed) {
        claimedRef.current = true;
        return;
      }
      // Denied. A colleague logging out on another machine may have a
      // /session-end still committing — wait briefly then re-claim once before
      // blocking the user with the modal.
      retryTimer = setTimeout(() => {
        requestSessionClaim(store, false).then(handleRetryResult);
      }, RECLAIM_RETRY_MS);
    });

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  // Re-check on the user's next interaction; force-logout if displaced.
  // recheckingRef collapses a near-simultaneous focus + route-change into one
  // in-flight call so a displaced user isn't logged out twice.
  const recheck = () => {
    if (!loggedIn || !claimedRef.current || recheckingRef.current) return;
    recheckingRef.current = true;
    requestSessionClaim(store, false).then((res) => {
      recheckingRef.current = false;
      if (!res.allowed) {
        claimedRef.current = false;
        // Log out FIRST (logout resets the store), THEN set the message so it
        // survives onto the login screen — same ordering as the session-expired
        // flow in useAppSessionGuard. timeout:0 keeps it visible until the user
        // dismisses it (they may be away from the desk when displaced).
        authStore.logout();
        utilsStore.setHeaderMessage(SESSION_DISPLACED_MESSAGE, HEADER_MESSAGE_TYPES.ERROR, 0);
      }
    });
  };

  // Route change.
  useEffect(() => {
    recheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Window focus (user returns to the tab).
  useEffect(() => {
    const onFocus = () => recheck();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  return null;
});

SessionClaimController.propTypes = {
  openModal: PropTypes.func.isRequired
};

export default SessionClaimController;
